import React from "react";

import { clamp } from "@kazoohr/helpers";

import Header from "../Header";
import { Loader } from "../Loader";
import { Tooltip } from "../Tooltip";
import { WidgetError } from "../WidgetError";

import { PercentageCircle } from "./PercentageCircle";
import styles from "./PercentageDonutChart.scss";

export interface PercentageDonutChartProps {
  maxPercent?: number;
  minPercent?: number;
  percent?: number;
  chartDescription?: string;
  tooltipContent?: null | React.ComponentType<{ percent: string }>;
  detailsContent?: null | React.ComponentType<{ percent: string }>;
  loading?: boolean;
  error?: boolean;
}

export function PercentageDonutChart({
  maxPercent = 1,
  minPercent = 0,
  percent = 0,
  chartDescription,
  tooltipContent,
  detailsContent,
  loading = false,
  error = false,
}: PercentageDonutChartProps): JSX.Element {
  const clampedPercent = clamp(minPercent, maxPercent, percent);
  const largePercent = clampedPercent * 100;

  const TooltipContentComponent = tooltipContent;
  const DetailsContentComponent = detailsContent;

  const calcPercent = (percent * 100).toFixed(0);
  const humanizedPercent = calcPercent + "%";

  if (loading) {
    return (
      <div data-testid="chart-loader">
        <Loader centered />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center" }}
        data-testid="chart-error"
      >
        <WidgetError text="There was an issue loading this chart" />
      </div>
    );
  }

  return (
    <div
      className={styles.progress__container}
      role="progressbar"
      aria-valuenow={largePercent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {TooltipContentComponent ? (
        <div data-testid="progress-chart-tooltip">
          <Tooltip
            content={<TooltipContentComponent percent={calcPercent} />}
            tooltipClassName={styles["chart-tooltip"]}
          >
            <div className={styles.progress__chart}>
              <PercentageCircle percent={clampedPercent} />
              <Header
                size="h3"
                className={styles.progress__percentage}
                level="3"
                data-testid="progress-chart-percentage-tooltip"
              >
                {humanizedPercent}
              </Header>
            </div>
          </Tooltip>
        </div>
      ) : (
        <div className={styles.progress__chart}>
          <PercentageCircle percent={clampedPercent} />
          <Header
            size="h3"
            className={styles.progress__percentage}
            level="3"
            data-testid="progress-chart-percentage-no-tooltip"
          >
            {humanizedPercent}
          </Header>
        </div>
      )}

      {chartDescription ? (
        <div
          className={styles.progress__text}
          data-testid="progress-chart-description"
        >
          <Header size="h6" level="6">
            {chartDescription}
          </Header>
        </div>
      ) : null}

      {DetailsContentComponent ? (
        <div data-testid="progress-chart-details">
          <DetailsContentComponent percent={calcPercent} />
        </div>
      ) : null}
    </div>
  );
}

export default PercentageDonutChart;
